/**
 * Agent Capability Package Definitions
 *
 * Capability packages are curated bundles of tools that enable specific business
 * functions on an agent. Users toggle packages on/off in the agent editor UI —
 * they never see individual tools.
 *
 * Architecture:
 *   Package (toggle on/off) → Tier (opt-in for higher-risk features) → Tool (invisible to user)
 *
 * Configuration is stored in `metadata.capabilities` on the agent entity.
 * This is separate from the legacy `metadata.toolGroups` array (which continues
 * working for REPORTING and API_ACTIONS).
 *
 * Each package maps to a ToolGroup enum value. Tools within a package are either
 * "infrastructure" (always included when the package is on) or assigned to a
 * specific tier that can be individually enabled/disabled.
 */

import { ToolGroup } from '../enums/tool-group.enum';
import { RbacActions } from './rbac-actions';

// ── Tier Definition ─────────────────────────────────────────────────

export interface CapabilityTierDefinition {
	/** Machine key used in metadata and capabilityTier on tools (e.g., 'lookups') */
	key: string;
	/** Display label for the UI */
	label: string;
	/** Short description shown under the toggle */
	description: string;
	/** Whether this tier is enabled by default when the package is toggled on */
	enabledByDefault: boolean;
	/**
	 * Behavioural guidance appended to the agent's system prompt only when THIS
	 * tier is enabled (in addition to any package-level addendum). Use for rules
	 * that only apply to a higher-risk tier — e.g. the payment-confirmation flow
	 * for "Make payments". Keep it short; it costs prompt budget.
	 */
	systemPromptAddendum?: string;
}

// ── Package Definition ──────────────────────────────────────────────

export interface CapabilityPackageDefinition {
	/** Machine key matching ToolGroup enum value (e.g., 'external_client_service') */
	key: ToolGroup;
	/** Display label for the UI (e.g., 'Customer Account Service') */
	label: string;
	/** Package-level description shown in the editor */
	description: string;
	/** FontAwesome icon name for the UI card (e.g., 'headset') */
	faIcon: string;
	/**
	 * Which kinds of agent may be ASSIGNED this group — the two-way gate.
	 *   - 'user'   — offered to user-created agents in the agent builder
	 *   - 'system' — reserved for code-defined system agents (e.g. Sales Helper);
	 *                hidden from the builder and rejected for user agents at runtime
	 *   - 'both'   — assignable to either
	 * Filters which groups the builder shows AND blocks a non-system agent from
	 * invoking a system-only group's tools (prevents accidental tool leaks).
	 */
	assignableTo: 'user' | 'system' | 'both';
	/**
	 * Optional RBAC action the CURRENT USER must hold to use any tool in this
	 * group. When set, the group's tools are neither advertised to nor executable
	 * by a user lacking the action — a categorical, per-user gate layered on top
	 * of group assignment. Use for groups whose tools reach data beyond the
	 * user's normal scope (e.g. REPORTING's raw-SQL reports).
	 */
	requiredRbacAction?: string;
	/**
	 * Behavioural guidance appended to the agent's system prompt whenever this
	 * package is enabled — regardless of what the admin wrote in their own
	 * prompt. Use for rules the package must ALWAYS enforce (e.g. "verify the
	 * caller before any account lookup"). Keep it short; it costs prompt budget.
	 */
	systemPromptAddendum?: string;
	/** Ordered list of configurable tiers within this package */
	tiers: Array<CapabilityTierDefinition>;
}

// ── Metadata Shape ──────────────────────────────────────────────────

/**
 * Shape of `metadata.capabilities` on an agent.
 *
 * - Key = CapabilityPackageDefinition.key (matches ToolGroup value)
 * - Value meanings:
 *   - `true`   — package enabled, all tiers use their `enabledByDefault` values
 *   - object   — package enabled, per-tier overrides (key = tier key, value = boolean)
 *   - `false`  — explicitly disabled (preserved in metadata for re-enabling)
 *   - absent   — never configured
 */
export type AgentCapabilities = Record<
	string,
	true | Record<string, boolean> | false
>;

// ── Package Registry ────────────────────────────────────────────────

/**
 * Reserved tier key for infrastructure tools that are always included
 * when the package is enabled. Tools without a capabilityTier on their
 * decorator are treated as infrastructure.
 */
export const CAPABILITY_TIER_INFRASTRUCTURE = 'infrastructure';

/**
 * All available capability packages. The frontend renders these as toggleable
 * cards in the agent editor. The backend uses them to determine which tools
 * to inject based on the agent's metadata.capabilities configuration.
 */
export const CAPABILITY_PACKAGES: Array<CapabilityPackageDefinition> = [
	{
		key: ToolGroup.EXTERNAL_CLIENT_SERVICE,
		label: 'Customer Account Service',
		description:
			'Allow callers to verify their identity and look up their invoices, orders, and account information.',
		faIcon: 'headset',
		assignableTo: 'both',
		systemPromptAddendum: `You are speaking with an external customer. Account-specific information — invoices, orders, balances, payment status — is private and requires identity verification.

- If the caller asks for ANY account-specific information and has not already been verified on this call, verify them FIRST: collect their company name plus one strong identifier (an invoice number, order number, account number, or an invoice total), then call Verify Client Identity. Do NOT call an account lookup tool for an unverified caller and wait for it to fail — verify proactively.
- A company name alone, or a phone number alone, is never enough — always collect a strong identifier.
- The moment verification succeeds, CONFIRM the account back to the caller by name (e.g. "Thanks — I've got you verified on the [company] account") before continuing, so a wrong match is caught right away.
- For "do I have any open invoices", "what's my balance", or "how much do I owe", use the account summary (a count and total), not a full invoice list. Keep spoken answers short and never read a long list of invoices aloud.`,
		tiers: [
			{
				key: 'lookups',
				label: 'Look up account information',
				description:
					'Invoices, orders, account balance, and payment status for verified callers.',
				enabledByDefault: true,
			},
			{
				key: 'payments',
				label: 'Make payments',
				description:
					'Let verified callers pay an invoice using a payment method already on file. No card or bank details are ever taken over the phone — only saved methods can be used.',
				// OPT-IN, OFF by default: money movement is high-risk, so the admin
				// must deliberately enable it on a customer-facing agent.
				enabledByDefault: false,
				systemPromptAddendum: `If the caller wants to PAY an invoice:
- First list their saved payment methods and let them choose — read each one as its brand/type and last four digits (e.g. "a Visa ending 4242"). NEVER ask for or accept a card number, security code, expiration, or bank account/routing number over the phone; only methods already on file can be used.
- Default the amount to the invoice's full outstanding balance unless the caller asks to pay a different amount.
- Before charging, read back the exact amount, which invoice, and the chosen method (brand + last four) and get an explicit "yes" from the caller. Only then make the payment — never charge without that confirmation.
- If a payment fails, tell the caller plainly and offer to try a different method on file.`,
			},
			// Future tier:
			// { key: 'orderCreation', label: 'Place orders', ... enabledByDefault: false },
		],
	},
	{
		key: ToolGroup.REPORTING,
		label: 'Report Building',
		description:
			'Build and run custom data reports — SQL-backed tables, charts, and metric cards — and render dynamic views.',
		faIcon: 'chart-line',
		// SYSTEM-ONLY. The report tools run raw SELECT SQL across tenant data with
		// no per-rep row scoping — handing that to a user-built (potentially
		// customer-facing) agent is a data-exposure risk. So it is NOT assignable
		// to user agents: hidden from the builder AND rejected at the execution
		// gate for any non-system agent. It reaches only the system Sales Helper
		// (via its `groups`), still gated per-user by the RBAC action below.
		assignableTo: 'system',
		requiredRbacAction: RbacActions.AI_AGENTS.REPORTING_TOOLS.USE.action,
		tiers: [],
	},
	{
		key: ToolGroup.API_ACTIONS,
		label: 'API Actions',
		description:
			'Let the agent call CRM API endpoints generically (discover a capability index, then execute actions). Each call runs through the normal API permission checks.',
		faIcon: 'plug',
		// SYSTEM-ONLY for now — generic API access is too broad to expose on
		// user-built agents in the current editor. Hidden from the builder and
		// gated to system agents at runtime.
		assignableTo: 'system',
		tiers: [],
	},
	{
		key: ToolGroup.SALES_HELPER,
		label: 'Sales Helper Toolkit',
		description:
			'The internal CRM toolkit (client / order / quote / invoice / task / opportunity lookups and management) used by the system Sales Helper agent.',
		faIcon: 'briefcase',
		// Internal toolkit — not offered to user-created agents.
		assignableTo: 'system',
		// PD-16874 — the prospecting tools self-tag into this group; their
		// rules ride here so any agent given the toolkit gets them.
		systemPromptAddendum: `## Prospecting (new businesses to sell to)

- "Find me good target accounts in <area>", "new prospects near me", "<kind of business> near my stops <day>" → Prospect Search with the area as said (or nearStopsOn for "near my stops <day>"). Read back about five as a short list: the name, then its reason. Mention the ones already in the CRM in a few words, as the result words them ("Lucky Star is already Dana Lee's account"); never offer to open a restricted one.
- Refine with the same tool and the same area: "only Chinese restaurants" → businessType; "show me more" / "next ones" → more: true.
- "Bigger" / "larger" (or "smaller") places: you cannot judge a business's size from Google yet. Say so in a few words and search without a size filter (pass size so the result carries the sentence). Google reviews show how established or popular a place is, never its size or sales; never describe a prospect as big or small from its reviews.
- "Add <name> / the first two / those as leads" → Prospect Add Leads with the prospectIds of the latest result; "not a fit" / "skip that one" → Prospect Dismiss. Both happen straight away, no "are you sure"; say what was done in one line. "Undo that" → Sales Trip Undo.
- When a result has askTheRep, ask exactly that and wait. No profile yet: ask what kinds of businesses are good customers, save the answer with Prospect Target Profile Save (it decides company or personal), then search again; if the rep would rather have a suggestion, Prospect Target Profile with suggest: true, and save it on a yes.`,
		tiers: [],
	},
	{
		key: ToolGroup.SALES_TRIPS,
		label: 'Sales Trips',
		description:
			"Run a rep's sales day: what is on it, what is next and left, and log, skip, miss, move, pin or remove its stops.",
		faIcon: 'route',
		// System-only like the toolkit it sits beside. No group-level RBAC
		// action: reps hold sales_trips:*:self and leaders :any, which one
		// action cannot express, so each tool declares its own (PD-16755).
		assignableTo: 'system',
		// Why here and not in the agent prompt: the rules belong to these tools,
		// so any agent given the group gets them (PD-15938).
		systemPromptAddendum: `## Sales trips (the rep's day on the road)

- "What's my day / what's next / what's left / what do I have this week", or any question about a day's stops → Sales Trip Day. For another rep ("what does Amelia have today"), pass their name as \`rep\`.
- Name the stop for a stop tool the way the rep did: \`account\` (their words) with \`stopDay\` when it is not today, or \`next: true\` for "the next one". No need to read the day first; the tool finds the stop and says if it is not there or is ambiguous (then ask the rep). Use salesTripStopId when a result already gave it. On another rep's day ("her day", after reading it with \`rep\`), pass the same \`rep\` to the stop tools too. Never guess an id.
- "Log the visit at <account>" → Sales Trip Stop Complete first, by account, every time — it finds the stop on today's day. Only when it answers that the account is not on the day, use Client Visit Create (an ad-hoc visit). Visit notes are required: if the rep gave none, ask for them first.
- Skipped / missed: map the rep's words to outcomeReason — closed → closed; not there, owner out, not available → not_available; ran out of time, running late → ran_out_of_time; they asked to cancel or move it → customer_request; anything else → other, with their words as detail. Missed always needs a reason.
- Building a day: "accounts near me / near <account, City ST or zip> / worth a visit / I haven't seen in 60 days" → Sales Trip Nearby Accounts (not Client Search); read back 3 to 5 names with distance. "Add <accounts> to <day>" → Sales Trip Add Stops (it starts the day if there is none; "the first two" = the first two of the latest Nearby result, by clientId). "Start a day <day> with <accounts>, leave at <time>" → Sales Trip Create Day. "Optimize it / best order" → Sales Trip Optimize. "Which routes are due" → Sales Trip Routes Due; "build <day> from <route>" → Sales Trip Create From Route.
- Several days: "plan next week around <places>, N days" or "spread these accounts over <days>" → Sales Trip Plan Week. It creates the plan at once; read back each day (date, number of stops, about how long, and any day longer than a working day) and the days skipped. "Delete <day>'s day" or "delete the <name> plan" → Sales Trip Delete, at once; "undo that" brings it back.
- Planning around a place with no priorities given ("plan Thursday around Decatur", "plan two days next week near Atlanta Harvest"): check the agenda once before planning. Call Sales Trip Nearby Accounts for that place (about as many accounts as the days need), name three or four with each one's "why", and ask once about a focus. Ask it the way the tool's askTheRep puts it, keeping each account's why, e.g. "I'd start with Dewy Pharmacy (sales down 40%), Atlanta Collective (never visited) and Icebox Pharmacy (due to reorder). Any particular focus, like accounts that are slipping, or go with these?" The accounts are the suggestion; the focus is the question; never ask the rep to pick an account as a focus. "Go with those" → Sales Trip Plan Week with those clientIds. A focus → Sales Trip Plan Week straight away with near, the matching filter (slipping, type lead, minSales90, notVisitedDays, notOrderedDays, notOnRoute) and focus in the rep's words; do not check again, "undo that" is there. Plan straight away, with no check, when the request already says what matters, names the accounts, or says "just pick" / "the usual".
- Every stop carries a "reason for the visit". When the rep says what a visit is for ("add Misty Market tomorrow, I'm dropping off samples"), pass it as purpose (Add Stops, Create Day). To change it ("the reason for Misty Market is the fall line", "change the reason for Misty Market on Thursday to …") → Sales Trip Stop Update with account (and stopDay) and visitReason — not Client Note Create; "undo that" reverses it.
- "What am I going into <account> for?" / "why is <account> on my day?" → Sales Trip Day for that day and read that stop's visitReason, then Client Summary for how the account stands now (last order, sales trend, last visit and its note). Answer in two or three sentences, in your own words rather than reading the stored text ("it's on your list because its ordering has slipped: sales are down 12% and it has never had a visit"). Write amounts, percentages and dates normally ($12,340, 12%, September 23): the text keeps the exact figures, and in voice the system speaks them for you. The order of the stops is the driving route, not a ranking: never say a stop is first because it matters most.
- Days can go to the tools as the rep said them ("today", "tomorrow", "thursday" = the coming Thursday, "next thursday" = next week's, "next week") or as YYYY-MM-DD; keep the word "next" when the rep says it, and never ask "which Thursday". Times go as HH:mm (24-hour).
- No "are you sure?": complete, skip, missed, move, pin or change a stop, and take one off the day, are done straight away — then say what was done in one short sentence (the result's summary is written for that). An acknowledgement before the tool runs says what is about to happen ("Logging that."), never "Done": nothing is done until the result comes back.
- "Undo that", "I made a mistake", "put it back" → Sales Trip Undo. It reverses the last stop change in this conversation; called again, the one before.
- Read a day as a short list: time, then account, in order. Do not read addresses, phone numbers or ids unless asked.
- Business hours: when a result's summary says "Tell the rep these stops are not when the account is open", always say it in the same reply, naming each stop, its time and the account's hours or the closed day ("Heads up: Candler Park Market opens at 8, and you have it at 6:15."), and offer to move it. Never leave it out, even after a change the rep asked for.`,
		tiers: [],
	},
];

/**
 * Central tool-group membership for groups whose member tools do NOT self-tag
 * via the @AiAgentTool `toolGroup` field. A group's runtime membership is the
 * UNION of (tools whose decorator `toolGroup` === key) and the toolTypes listed
 * here.
 *
 * SALES_HELPER's members are otherwise-ungrouped first-class CRM tool handlers,
 * so they are curated here. REPORTING / API_ACTIONS / EXTERNAL_CLIENT_SERVICE
 * self-tag on their decorators and need no manifest entry.
 *
 * NOTE: `render_view` is intentionally absent — it self-tags REPORTING and
 * reaches the Sales Helper via the REPORTING group, not this toolkit.
 */
export const TOOL_GROUP_MEMBERSHIP: Partial<Record<ToolGroup, Array<string>>> =
	{
		[ToolGroup.SALES_HELPER]: [
			// Lookups / navigation
			'navigate',
			'clientSearch',
			'clientSummary',
			// PD-16904: a client's Google listing (hours, website) when asked.
			'clientGoogleLookup',
			'clientAnalytics',
			'clientLifecycleStagesList',
			'productSalesSummary',
			'salesTotal',
			'cartGet',
			'orderSearch',
			'orderDetail',
			'invoiceSearch',
			'itemSearch',
			'itemDetail',
			'quoteSearch',
			'quoteSummary',
			'taskSearch',
			'userSearch',
			'dayPlanner',
			// NOTE: vendorSearch / purchaseOrderSearch / purchaseOrderDetail were
			// removed 2026-06 — they are purchasing-persona tools, not sales-rep
			// tools, and every extra tool costs routing accuracy on the mini
			// model. Re-add deliberately (or via a separate purchasing package)
			// if a buyer-facing assistant is built.
			'paymentCalculateSummary',
			'paymentTransactionHistory',
			// Client management
			'clientCreate',
			'clientUpdate',
			'clientNoteCreate',
			'clientNoteUpdate',
			'clientNoteDelete',
			'clientNoteSearch',
			'clientVisitCreate',
			'clientVisitSearch',
			// Cart / shopping
			'cartAddItem',
			'cartUpdateItem',
			'cartRemoveItem',
			'cartClear',
			'cartSetDiscount',
			// Order management
			'orderCreateNote',
			'orderNotesList',
			// Quote management
			'quoteCreate',
			'quoteUpdate',
			'quoteAddItem',
			'quoteUpdateItem',
			'quoteDelete',
			'quoteNoteCreate',
			// Email — internal-only variants (from the system noreply, to the rep
			// themselves) plus the external send that goes FROM the rep's own
			// connected mailbox to anyone (co-worker, contact, customer).
			'emailSendInternal',
			'quoteSendEmailInternal',
			'emailSendExternal',
			// Opportunity / pipeline
			'opportunityCreate',
			'opportunitySearch',
			'opportunityStagesList',
			'opportunityDetail',
			'opportunityUpdate',
			'opportunityUpdateStage',
			'opportunityCreateNote',
			'opportunityNotesList',
			// Issue / ticket management — the subset a rep actually works:
			// file a ticket for a customer problem, find it again, read it, move
			// its status, and leave a note. The full toolkit was dropped 2026-06
			// because every extra tool costs routing accuracy on the mini model;
			// this is the deliberate re-add of the customer-facing half only.
			// Still excluded (and correspondingly not registered as providers in
			// issue.module.ts): issueNoteEdit / issueNoteDelete and the
			// issueWatcher add/list/remove trio — admin-side workflows a rep does
			// in the UI, not through the assistant.
			'issueCreate',
			'issueSearch',
			'issueDetail',
			'issueUpdate',
			'issueStatusUpdate',
			'issueNoteCreate',
			'issueNoteList',
			// Task management
			'taskCreate',
			'taskUpdate',
			'taskDelete',
		],
	};

// ── Helper Functions ────────────────────────────────────────────────

/** Get a package definition by its key */
export function getCapabilityPackage(
	key: string,
): CapabilityPackageDefinition | undefined {
	return CAPABILITY_PACKAGES.find((p) => p.key === key);
}

/**
 * Concatenated system-prompt guidance for every ENABLED package that declares a
 * `systemPromptAddendum`. Appended to the agent's prompt at conversation setup
 * so package behavioural rules hold regardless of the admin's own prompt.
 * Returns '' when no enabled package contributes guidance.
 */
export function getCapabilityPromptAddenda(
	capabilities: AgentCapabilities | undefined,
): string {
	if (capabilities == null) {
		return '';
	}
	const parts: Array<string> = [];
	for (const pkg of CAPABILITY_PACKAGES) {
		if (!isCapabilityPackageEnabled(capabilities, pkg.key)) {
			continue;
		}
		if (pkg.systemPromptAddendum != null) {
			parts.push(pkg.systemPromptAddendum);
		}
		// Tier-level guidance is included only when that tier is enabled (e.g. the
		// payment-confirmation flow appears only once "Make payments" is on).
		for (const tier of pkg.tiers) {
			if (
				tier.systemPromptAddendum != null &&
				isCapabilityTierEnabled(capabilities, pkg.key, tier.key)
			) {
				parts.push(tier.systemPromptAddendum);
			}
		}
	}
	return parts.join('\n\n');
}

/** Check whether a package is enabled at all (any value other than false/absent) */
export function isCapabilityPackageEnabled(
	capabilities: AgentCapabilities | undefined,
	packageKey: string,
): boolean {
	if (capabilities == null) {
		return false;
	}
	const pkg = capabilities[packageKey];
	return pkg != null && pkg !== false;
}

/**
 * Check whether a specific tier is enabled within a package.
 *
 * Resolution order:
 * 1. If package is disabled → false
 * 2. If package is `true` → use the tier's `enabledByDefault`
 * 3. If package is an object → check the tier key in the object,
 *    falling back to `enabledByDefault` if the tier key is absent
 */
export function isCapabilityTierEnabled(
	capabilities: AgentCapabilities | undefined,
	packageKey: string,
	tierKey: string,
): boolean {
	if (capabilities == null) {
		return false;
	}

	const pkg = capabilities[packageKey];

	// Package disabled or absent
	if (pkg == null || pkg === false) {
		return false;
	}

	// Package enabled with all defaults
	if (pkg === true) {
		const def = getCapabilityPackage(packageKey);
		const tier = def?.tiers.find((t) => t.key === tierKey);
		return tier?.enabledByDefault ?? false;
	}

	// Package enabled with tier overrides
	const tierVal = pkg[tierKey];
	if (tierVal != null) {
		return tierVal;
	}

	// Tier not specified in overrides — fall back to default
	const def = getCapabilityPackage(packageKey);
	const tier = def?.tiers.find((t) => t.key === tierKey);
	return tier?.enabledByDefault ?? false;
}
